<script lang="ts">
	import { _ } from 'svelte-i18n';
	import Header from '$lib/components/sections/Header.svelte';
	import Footer from '$lib/components/sections/Footer.svelte';
	import Button from '$lib/components/primitives/Button.svelte';
	import Card from '$lib/components/primitives/Card.svelte';
	import Icon from '$lib/components/primitives/Icon.svelte';

	let quickStartSteps = $derived([
		{
			number: '1',
			title: $_('gettingStarted.quickStart.step1.title'),
			description: $_('gettingStarted.quickStart.step1.description'),
			icon: 'user-plus',
			action: {
				label: $_('gettingStarted.quickStart.step1.action'),
				href: 'https://app.heyweek.com/?signup'
			}
		},
		{
			number: '2',
			title: $_('gettingStarted.quickStart.step2.title'),
			description: $_('gettingStarted.quickStart.step2.description'),
			icon: 'settings',
			action: {
				label: $_('gettingStarted.quickStart.step2.action'),
				href: '/docs/workspaces'
			}
		},
		{
			number: '3',
			title: $_('gettingStarted.quickStart.step3.title'),
			description: $_('gettingStarted.quickStart.step3.description'),
			icon: 'folder-plus',
			action: {
				label: $_('gettingStarted.quickStart.step3.action'),
				href: '/docs/projects'
			}
		},
		{
			number: '4',
			title: $_('gettingStarted.quickStart.step4.title'),
			description: $_('gettingStarted.quickStart.step4.description'),
			icon: 'clock',
			action: {
				label: $_('gettingStarted.quickStart.step4.action'),
				href: '/docs/time-tracking'
			}
		}
	]);

	let setupGuides = $derived([
		{
			title: $_('gettingStarted.guides.profitability.title'),
			icon: 'user',
			description: $_('gettingStarted.guides.profitability.description'),
			timeToComplete: $_('gettingStarted.guides.profitability.time'),
			steps: [
				$_('gettingStarted.guides.profitability.step1'),
				$_('gettingStarted.guides.profitability.step2'),
				$_('gettingStarted.guides.profitability.step3'),
				$_('gettingStarted.guides.profitability.step4'),
				$_('gettingStarted.guides.profitability.step5')
			],
			href: '/guides/freelancer-setup'
		},
		{
			title: $_('gettingStarted.guides.cashflow.title'),
			icon: 'users',
			description: $_('gettingStarted.guides.cashflow.description'),
			timeToComplete: $_('gettingStarted.guides.cashflow.time'),
			steps: [
				$_('gettingStarted.guides.cashflow.step1'),
				$_('gettingStarted.guides.cashflow.step2'),
				$_('gettingStarted.guides.cashflow.step3'),
				$_('gettingStarted.guides.cashflow.step4'),
				$_('gettingStarted.guides.cashflow.step5')
			],
			href: '/guides/financial-workflow'
		},
		{
			title: $_('gettingStarted.guides.team.title'),
			icon: 'briefcase',
			description: $_('gettingStarted.guides.team.description'),
			timeToComplete: $_('gettingStarted.guides.team.time'),
			steps: [
				$_('gettingStarted.guides.team.step1'),
				$_('gettingStarted.guides.team.step2'),
				$_('gettingStarted.guides.team.step3'),
				$_('gettingStarted.guides.team.step4'),
				$_('gettingStarted.guides.team.step5')
			],
			href: '/guides/team-setup'
		}
	]);

	let essentialFeatures = $derived([
		{
			category: $_('gettingStarted.features.timeManagement.title'),
			features: [
				{
					title: $_('gettingStarted.features.timeManagement.timer.title'),
					description: $_('gettingStarted.features.timeManagement.timer.description'),
					icon: 'clock',
					href: '/features/time-tracking'
				},
				{
					title: $_('gettingStarted.features.timeManagement.calendar.title'),
					description: $_('gettingStarted.features.timeManagement.calendar.description'),
					icon: 'calendar',
					href: '/integrations'
				},
				{
					title: $_('gettingStarted.features.timeManagement.mobile.title'),
					description: $_('gettingStarted.features.timeManagement.mobile.description'),
					icon: 'smartphone',
					href: '/products/mobile'
				}
			]
		},
		{
			category: $_('gettingStarted.features.projectClient.title'),
			features: [
				{
					title: $_('gettingStarted.features.projectClient.projects.title'),
					description: $_('gettingStarted.features.projectClient.projects.description'),
					icon: 'folder',
					href: '/features/project-management'
				},
				{
					title: $_('gettingStarted.features.projectClient.portal.title'),
					description: $_('gettingStarted.features.projectClient.portal.description'),
					icon: 'users',
					href: '/features/team-support'
				},
				{
					title: $_('gettingStarted.features.projectClient.collaboration.title'),
					description: $_('gettingStarted.features.projectClient.collaboration.description'),
					icon: 'message-square',
					href: '/features/communication'
				}
			]
		},
		{
			category: $_('gettingStarted.features.billingFinance.title'),
			features: [
				{
					title: $_('gettingStarted.features.billingFinance.invoicing.title'),
					description: $_('gettingStarted.features.billingFinance.invoicing.description'),
					icon: 'file-text',
					href: '/features/invoicing'
				},
				{
					title: $_('gettingStarted.features.billingFinance.expenses.title'),
					description: $_('gettingStarted.features.billingFinance.expenses.description'),
					icon: 'dollar-sign',
					href: '/features/finance'
				},
				{
					title: $_('gettingStarted.features.billingFinance.banking.title'),
					description: $_('gettingStarted.features.billingFinance.banking.description'),
					icon: 'credit-card',
					href: '/integrations'
				}
			]
		}
	]);

	let whyDifferent = $derived([
		{
			title: $_('gettingStarted.whyDifferent.workspace.title'),
			icon: 'layers',
			description: $_('gettingStarted.whyDifferent.workspace.description')
		},
		{
			title: $_('gettingStarted.whyDifferent.connected.title'),
			icon: 'link',
			description: $_('gettingStarted.whyDifferent.connected.description')
		},
		{
			title: $_('gettingStarted.whyDifferent.financial.title'),
			icon: 'trending-up',
			description: $_('gettingStarted.whyDifferent.financial.description')
		},
		{
			title: $_('gettingStarted.whyDifferent.teamFeed.title'),
			icon: 'message-square',
			description: $_('gettingStarted.whyDifferent.teamFeed.description')
		}
	]);

	let resources = $derived([
		{
			title: $_('gettingStarted.resources.docs.title'),
			icon: 'book-open',
			description: $_('gettingStarted.resources.docs.description'),
			href: '/docs'
		},
		{
			title: $_('gettingStarted.resources.blog.title'),
			icon: 'file-text',
			description: $_('gettingStarted.resources.blog.description'),
			href: '/blog'
		},
		{
			title: $_('gettingStarted.resources.support.title'),
			icon: 'life-buoy',
			description: $_('gettingStarted.resources.support.description'),
			href: '/contact'
		}
	]);
</script>

<svelte:head>
	<title>{$_('gettingStarted.meta.title')}</title>
	<meta name="description" content={$_('gettingStarted.meta.description')} />
	<meta property="og:title" content="{$_('gettingStarted.meta.title')}" />
	<meta property="og:description" content={$_('gettingStarted.meta.description')} />
</svelte:head>

<Header />

<div class="getting-started-page">
	<!-- Hero Section -->
	<section class="hero">
		<div class="container">
			<div class="hero__content">
				<div class="hero__badge">
					<Icon name="rocket" size={16} />
					<span>{$_('gettingStarted.hero.badge')}</span>
				</div>
				<h1 class="hero__title">
					{$_('gettingStarted.hero.title')}
				</h1>
				<p class="hero__subtitle">
					{$_('gettingStarted.hero.subtitle')}
				</p>
				<div class="hero__actions">
					<Button href="https://app.heyweek.com/?signup" variant="primary" size="lg">
						{$_('gettingStarted.hero.cta.primary')}
					</Button>
					<Button href="#quick-start" variant="ghost" size="lg">
						{$_('gettingStarted.hero.cta.secondary')}
					</Button>
				</div>
			</div>
		</div>
	</section>

	<!-- Quick Start Steps -->
	<section id="quick-start" class="quick-start">
		<div class="container">
			<div class="section__header">
				<h2 class="section__title">{$_('gettingStarted.quickStart.title')}</h2>
				<p class="section__subtitle">
					{$_('gettingStarted.quickStart.subtitle')}
				</p>
			</div>

			<div class="steps__grid">
				{#each quickStartSteps as step}
					<Card class="step-card">
						<div class="step-card__number">{step.number}</div>
						<div class="step-card__icon">
							<Icon name={step.icon} size={24} />
						</div>
						<h3 class="step-card__title">{step.title}</h3>
						<p class="step-card__description">{step.description}</p>
						<Button href={step.action.href} variant="outline" size="sm" external={step.action.href.startsWith('http')}>
							{step.action.label}
						</Button>
					</Card>
				{/each}
			</div>
		</div>
	</section>

	<!-- Why Different Section -->
	<section class="why-different">
		<div class="container">
			<div class="section__header">
				<h2 class="section__title">{$_('gettingStarted.whyDifferent.title')}</h2>
				<p class="section__subtitle">
					{$_('gettingStarted.whyDifferent.subtitle')}
				</p>
			</div>

			<div class="different__grid">
				{#each whyDifferent as item}
					<div class="different-item">
						<div class="different-item__icon">
							<Icon name={item.icon} size={24} />
						</div>
						<div class="different-item__content">
							<h3 class="different-item__title">{item.title}</h3>
							<p class="different-item__description">{item.description}</p>
						</div>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- Setup Guides -->
	<section class="setup-guides">
		<div class="container">
			<div class="section__header">
				<h2 class="section__title">{$_('gettingStarted.guides.title')}</h2>
				<p class="section__subtitle">
					{$_('gettingStarted.guides.subtitle')}
				</p>
			</div>

			<div class="guides__grid">
				{#each setupGuides as guide}
					<Card class="guide-card" hoverable>
						<div class="guide-card__header">
							<div class="guide-card__icon">
								<Icon name={guide.icon} size={28} />
							</div>
							<div class="guide-card__meta">
								<Icon name="clock" size={14} />
								<span>{guide.timeToComplete}</span>
							</div>
						</div>
						<h3 class="guide-card__title">{guide.title}</h3>
						<p class="guide-card__description">{guide.description}</p>
						<ul class="guide-card__steps">
							{#each guide.steps as step}
								<li>
									<Icon name="check" size={14} />
									<span>{step}</span>
								</li>
							{/each}
						</ul>
						<Button href={guide.href} variant="outline" size="sm" class="guide-card__button">
							{$_('gettingStarted.guides.viewGuide')}
							<Icon name="arrow-right" size={16} />
						</Button>
					</Card>
				{/each}
			</div>
		</div>
	</section>

	<!-- Essential Features -->
	<section class="essential-features">
		<div class="container">
			<div class="section__header">
				<h2 class="section__title">{$_('gettingStarted.features.title')}</h2>
				<p class="section__subtitle">
					{$_('gettingStarted.features.subtitle')}
				</p>
			</div>

			{#each essentialFeatures as category}
				<div class="feature-category">
					<h3 class="category__title">{category.category}</h3>
					<div class="features__grid">
						{#each category.features as feature}
							<a href={feature.href} class="feature-card">
								<div class="feature-card__icon">
									<Icon name={feature.icon} size={20} />
								</div>
								<div class="feature-card__content">
									<h4 class="feature-card__title">{feature.title}</h4>
									<p class="feature-card__description">{feature.description}</p>
								</div>
								<span class="feature-card__arrow">
									<Icon name="arrow-right" size={16} />
								</span>
							</a>
						{/each}
					</div>
				</div>
			{/each}
		</div>
	</section>

	<!-- Resources -->
	<section class="resources">
		<div class="container">
			<div class="section__header">
				<h2 class="section__title">{$_('gettingStarted.resources.title')}</h2>
				<p class="section__subtitle">
					{$_('gettingStarted.resources.subtitle')}
				</p>
			</div>

			<div class="resources__grid">
				{#each resources as resource}
					<a href={resource.href} class="resource-card" target={resource.href.startsWith('http') ? '_blank' : undefined}>
						<div class="resource-card__icon">
							<Icon name={resource.icon} size={24} />
						</div>
						<h3 class="resource-card__title">{resource.title}</h3>
						<p class="resource-card__description">{resource.description}</p>
					</a>
				{/each}
			</div>

			<div class="support-banner">
				<div class="support-banner__content">
					<Icon name="help-circle" size={32} />
					<div>
						<h3>{$_('gettingStarted.support.title')}</h3>
						<p>{$_('gettingStarted.support.description')}</p>
					</div>
					<Button href="/contact" variant="primary" size="sm">
						{$_('gettingStarted.support.button')}
					</Button>
				</div>
			</div>
		</div>
	</section>
</div>

<Footer />

<style>
	.getting-started-page {
		min-height: 100vh;
		padding-top: var(--space-8);
	}

	:global(.container) {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 1rem;
		width: 100%;
	}

	/* Hero Section */
	.hero {
		padding: var(--space-16) 0;
		background: linear-gradient(180deg, var(--bg-secondary) 0%, transparent 100%);
		text-align: center;
	}

	.hero__content {
		max-width: 800px;
		margin: 0 auto;
	}

	.hero__badge {
		display: inline-flex;
		align-items: center;
		gap: var(--space-2);
		padding: var(--space-1) var(--space-3);
		background: rgba(255, 94, 126, 0.1);
		color: rgb(255, 94, 126);
		border-radius: var(--radius-full);
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-medium);
		margin-bottom: var(--space-4);
	}

	:global(.dark) .hero__badge {
		background: rgba(255, 94, 126, 0.15);
		color: rgb(255, 140, 160);
	}

	.hero__title {
		font-size: clamp(2.5rem, 5vw, 3.5rem);
		font-weight: var(--font-weight-bold);
		line-height: 1.1;
		color: var(--text-primary);
		margin-bottom: var(--space-6);
		letter-spacing: -0.02em;
	}

	.hero__subtitle {
		font-size: var(--font-size-xl);
		color: var(--text-secondary);
		line-height: 1.6;
		max-width: 600px;
		margin: 0 auto var(--space-8);
	}

	.hero__actions {
		display: flex;
		gap: var(--space-4);
		justify-content: center;
		flex-wrap: wrap;
	}

	/* Section Headers */
	.section__header {
		text-align: center;
		margin-bottom: var(--space-12);
	}

	.section__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}

	.section__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		max-width: 600px;
		margin: 0 auto;
	}

	/* Quick Start Steps */
	.quick-start {
		padding: var(--space-20) 0;
	}

	.steps__grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
		gap: var(--space-6);
	}

	:global(.step-card) {
		padding: var(--space-6);
		text-align: center;
		position: relative;
		border: 1px solid var(--border-light);
	}

	.step-card__number {
		position: absolute;
		top: var(--space-4);
		left: var(--space-4);
		width: 32px;
		height: 32px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(255, 94, 126, 0.1);
		color: rgb(255, 94, 126);
		border-radius: var(--radius-full);
		font-weight: var(--font-weight-bold);
		font-size: var(--font-size-lg);
	}

	.step-card__icon {
		width: 48px;
		height: 48px;
		margin: 0 auto var(--space-4);
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(255, 94, 126, 0.1);
		color: rgb(255, 94, 126);
		border-radius: var(--radius-lg);
	}

	:global(.dark) .step-card__icon {
		background: rgba(255, 94, 126, 0.15);
		color: rgb(255, 140, 160);
	}

	.step-card__title {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-2);
	}

	.step-card__description {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		margin-bottom: var(--space-4);
		line-height: 1.6;
	}

	/* Why Different Section */
	.why-different {
		padding: var(--space-20) 0;
		background: var(--bg-secondary);
	}

	.different__grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
		gap: var(--space-8);
		margin-top: var(--space-12);
	}

	.different-item {
		display: flex;
		gap: var(--space-4);
	}

	.different-item__icon {
		flex-shrink: 0;
		width: 48px;
		height: 48px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: linear-gradient(135deg, 
			rgba(255, 94, 126, 0.1) 0%, 
			rgba(151, 71, 255, 0.1) 100%);
		border-radius: var(--radius-lg);
		color: var(--accent-primary);
	}

	:global(.dark) .different-item__icon {
		background: linear-gradient(135deg, 
			rgba(255, 94, 126, 0.15) 0%, 
			rgba(151, 71, 255, 0.15) 100%);
	}

	.different-item__content {
		flex: 1;
	}

	.different-item__title {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-2);
	}

	.different-item__description {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		line-height: 1.6;
	}

	/* Setup Guides */
	.setup-guides {
		padding: var(--space-20) 0;
	}

	.guides__grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
		gap: var(--space-8);
	}

	:global(.guide-card) {
		padding: var(--space-8);
		border: 1px solid var(--border-light);
	}

	.guide-card__header {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		margin-bottom: var(--space-4);
	}

	.guide-card__icon {
		width: 56px;
		height: 56px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(255, 94, 126, 0.1);
		color: rgb(255, 94, 126);
		border-radius: var(--radius-lg);
	}

	:global(.dark) .guide-card__icon {
		background: rgba(255, 94, 126, 0.15);
		color: rgb(255, 140, 160);
	}

	.guide-card__meta {
		display: flex;
		align-items: center;
		gap: var(--space-1);
		padding: var(--space-1) var(--space-2);
		background: var(--bg-tertiary);
		border-radius: var(--radius-md);
		font-size: var(--font-size-xs);
		color: var(--text-secondary);
	}

	.guide-card__title {
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-2);
	}

	.guide-card__description {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		margin-bottom: var(--space-4);
	}

	.guide-card__steps {
		list-style: none;
		padding: 0;
		margin: 0 0 var(--space-6) 0;
	}

	.guide-card__steps li {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		padding: var(--space-2) 0;
		font-size: var(--font-size-sm);
		color: var(--text-primary);
	}

	.guide-card__steps li :global(svg) {
		color: rgb(34, 197, 94);
		flex-shrink: 0;
	}

	:global(.guide-card__button) {
		width: 100%;
		justify-content: space-between;
	}

	/* Essential Features */
	.essential-features {
		padding: var(--space-20) 0;
	}

	.feature-category {
		margin-bottom: var(--space-12);
	}

	.feature-category:last-child {
		margin-bottom: 0;
	}

	.category__title {
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-6);
		padding-bottom: var(--space-3);
		border-bottom: 1px solid var(--border-light);
	}

	.features__grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
		gap: var(--space-4);
	}

	.feature-card {
		display: flex;
		align-items: center;
		gap: var(--space-4);
		padding: var(--space-4);
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
		text-decoration: none;
		transition: all 0.2s ease;
	}

	.feature-card:hover {
		transform: translateX(4px);
		border-color: rgb(255, 94, 126);
		box-shadow: var(--shadow-md);
	}

	:global(.dark) .feature-card {
		background: var(--bg-tertiary);
		border-color: var(--border-default);
	}

	.feature-card__icon {
		width: 40px;
		height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(255, 94, 126, 0.1);
		color: rgb(255, 94, 126);
		border-radius: var(--radius-md);
		flex-shrink: 0;
	}

	:global(.dark) .feature-card__icon {
		background: rgba(255, 94, 126, 0.15);
		color: rgb(255, 140, 160);
	}

	.feature-card__content {
		flex: 1;
	}

	.feature-card__title {
		font-size: var(--font-size-base);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-1);
	}

	.feature-card__description {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
	}

	.feature-card__arrow {
		color: var(--text-tertiary);
		flex-shrink: 0;
		display: flex;
		align-items: center;
	}

	/* Resources */
	.resources {
		padding: var(--space-20) 0;
		background: var(--bg-secondary);
	}

	.resources__grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
		gap: var(--space-6);
		margin-bottom: var(--space-12);
	}

	.resource-card {
		padding: var(--space-6);
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
		text-align: center;
		text-decoration: none;
		transition: all 0.2s ease;
	}

	.resource-card:hover {
		transform: translateY(-4px);
		border-color: rgb(255, 94, 126);
		box-shadow: var(--shadow-elevated);
	}

	:global(.dark) .resource-card {
		background: var(--bg-tertiary);
		border-color: var(--border-default);
	}

	.resource-card__icon {
		width: 48px;
		height: 48px;
		margin: 0 auto var(--space-4);
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(255, 94, 126, 0.1);
		color: rgb(255, 94, 126);
		border-radius: var(--radius-lg);
	}

	:global(.dark) .resource-card__icon {
		background: rgba(255, 94, 126, 0.15);
		color: rgb(255, 140, 160);
	}

	.resource-card__title {
		font-size: var(--font-size-base);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-2);
	}

	.resource-card__description {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
	}

	/* Support Banner */
	.support-banner {
		padding: var(--space-8);
		background: linear-gradient(135deg, rgba(255, 94, 126, 0.1), rgba(255, 120, 150, 0.1));
		border: 1px solid rgba(255, 94, 126, 0.2);
		border-radius: var(--radius-xl);
	}

	:global(.dark) .support-banner {
		background: linear-gradient(135deg, rgba(255, 94, 126, 0.15), rgba(255, 120, 150, 0.15));
		border-color: rgba(255, 94, 126, 0.3);
	}

	.support-banner__content {
		display: flex;
		align-items: center;
		gap: var(--space-6);
	}

	.support-banner__content :global(svg) {
		color: rgb(255, 94, 126);
		flex-shrink: 0;
	}

	.support-banner__content > div {
		flex: 1;
	}

	.support-banner__content h3 {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-1);
	}

	.support-banner__content p {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
	}

	/* Responsive */
	@media (max-width: 768px) {
		.hero__title {
			font-size: 2rem;
		}

		.steps__grid {
			grid-template-columns: 1fr;
		}

		.guides__grid {
			grid-template-columns: 1fr;
		}

		.features__grid {
			grid-template-columns: 1fr;
		}

		.resources__grid {
			grid-template-columns: repeat(2, 1fr);
		}

		.support-banner__content {
			flex-direction: column;
			text-align: center;
		}
	}
</style>